/**
 * StandRevisionDetailPage.jsx — Revizyon Konuşması (Stand Listesi tarafı)
 * Route: /Management/Projects/Stands/:standId/revisions/:revisionId
 * Roller: [1,2,3]
 *
 * ProjectRevisionDetailPage.jsx'in (Projeler Listesi/Proje Detayı — başka bir
 * geliştiricinin sayfaları, dokunulmuyor) Stand Listesi karşılığı. Proje kaydına
 * doğrudan id ile değil, standId köprüsüyle ulaşılır (bkz. StandOverviewDetailPage.jsx
 * başındaki 2026-07-18 notu): standId route param'ı mongo _id'dir, önce
 * getStandOverviewDetail ile çözülür, oradaki standId (numeric) alanı
 * getProjectByStandId'ye verilir.
 *
 * 2026-08-25 revizyonu — TAMPON (buffer). Bir revizyon artık sadece bir konuşma
 * değil, KABUL BEKLEYEN BİR DEĞİŞİKLİK (bkz. docs/guides/
 * FE-Revisions-and-KVKK-update-guide.md ve projectService.js bölüm 9). Bu sayfa
 * BİZİM tarafımız, yani kararın verildiği yer:
 *   - status ÜÇ değerli: open (sıra onlarda) / answered (sıra BİZDE) / resolved.
 *   - `revision.proposed` doluyken projede hiçbir şey değişmemiştir; değeri projeye
 *     yazan TEK çağrı acceptRevision'dır.
 *   - Kabul üç şekilde olur: onların değeriyle (APPLIED), kendi değerimizle
 *     (APPLIED_WITH_EDIT), ya da hiçbir şey yazmadan (ACKNOWLEDGED — belge ve
 *     serbest not revizyonlarında TEK seçenek).
 *   - Reddetmek revizyonu ÖLDÜRMEZ, gerekçeyle OPEN'a döndürür. Bir talebe tümden
 *     "hayır" demek için "Çözüldü İşaretle" kullanılır (o outcome yazmaz).
 *   - ⚠️ Stand tipi / beyan edilen yükseklik kabul edilirse proje SİSTEM ATAMASI
 *     kuyruğuna geri döner (requiresSystemReassign) — onay modalında uyarılır.
 *
 * KURAL: mutasyon sonrası aynı iki adımlı load() ile reload edilir — ayrı bir
 *   getRevision endpoint'i yok.
 * NOT: üç ayrı "bulunamadı" durumu var: stand bulunamadı (throw → Alert), stand var ama
 *   standId köprüsü hiçbir projeye bağlanmıyor (yumuşak — "proje kaydı yok" Alert'i),
 *   proje var ama bu revizyon yok (silinmiş/eski link — "Revizyon bulunamadı" Alert'i).
 *   Son ikisi Standa Dön'e yönlendirir.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container, Card, CardBody, CardHeader,
  Row, Col, Button, Badge,
  Modal, ModalHeader, ModalBody, ModalFooter,
  Alert, Spinner, Input, Label, FormGroup,
} from "reactstrap";
import {
  ArrowLeft, RefreshCw, Trash2, CheckCircle, RotateCcw, Send,
  ThumbsUp, Pencil, XCircle, AlertTriangle,
} from "lucide-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import RevisionThread, { fmtRevisionValue } from "../../../Components/Projects/RevisionThread";
import { getStandOverviewDetail } from "../../../services/standOverviewService";
import {
  getProjectByStandId, addRevisionMessage, setRevisionStatus, deleteRevision, getAllStandFiles,
  getRevisableFields, acceptRevision, rejectRevision,
  REVISION_TARGET_KIND, REVISION_STATUS, REVISION_OPENED_BY,
} from "../../../services/projectService";
import KuralIhlalListesi from "../../../Components/Projects/KuralIhlalListesi";
import { useTranslation } from "react-i18next";
import { currentLocaleTag } from "../../../i18n";

// Mock stand id'leri Mongo-tarzı hex string'lerdir; gerçek backend'in standId'leri
// sayısaldır — bkz. StandOverviewDetailPage.jsx'teki aynı isimli sabit.
const isNumericStandId = (v) => /^\d+$/.test(String(v));

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(currentLocaleTag(), {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function resolveFileName(standFileId, project, allStandFiles) {
  if (standFileId == null) return null;
  const fromRequired = (project.requiredFiles || []).find(f => f.stand_file_id === standFileId);
  if (fromRequired) return fromRequired.stand_file_name;
  const fromAll = (allStandFiles || []).find(f => f.stand_file_id === standFileId);
  return fromAll?.stand_file_name ?? `Belge Tipi #${standFileId}`;
}

// Kural ihlalleri bu controller'ın diğer 400'lerinin aksine JSON dizisi olarak gelir
// (parseError bunları Error'a iliştirir) — tek satıra sıkıştırmak yerine hepsi
// listelenir, yoksa dört problem dört gidiş-geliş demek olur.
//
// ⚠️ Liste elle değil KuralIhlalListesi ile basılıyor: 2026-08-28'den beri üçüncü bir
//    severity var (PERMITTED — onaylı bir Özel İzin'in temizlediği gerçek bir ihlal) ve
//    düz bir <li> listesi onu diğer hatalardan ayırt edemiyordu.
function ErrorAlert({ error, onDismiss }) {
  const { t } = useTranslation("projeler");
  if (!error) return null;
  const hasViolations = Array.isArray(error.violations) && error.violations.length > 0;
  return (
    <>
      <Alert color="danger" toggle={onDismiss}>
        <div>{error.message}</div>
      </Alert>
      {hasViolations && (
        <KuralIhlalListesi
          violations={error.violations}
          title={t("standRevisionDetail.onerilenDegerBuFuarinKurallarina")}
        />
      )}
    </>
  );
}

export default function StandRevisionDetailPage() {
  const { t } = useTranslation("projeler");
  const { standId, revisionId } = useParams();
  const navigate = useNavigate();
  const isLiveStandId = isNumericStandId(standId);

  const [stand, setStand] = useState(null);
  const [project, setProject] = useState(null);
  const [allStandFiles, setAllStandFiles] = useState([]);
  const [revisableFields, setRevisableFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);

  const [replyText, setReplyText] = useState("");
  const [deleteModal, setDeleteModal] = useState(false);

  // Kabul modalı — mode: 'apply' (onların değeri) | 'edit' (bizim değerimiz) |
  // 'ack' (hiçbir şey yazma). Tek modal, çünkü üçü de aynı onay ekranının
  // varyasyonu; ayrı ayrı modal üç kez aynı uyarıyı kopyalamak olurdu.
  const [acceptMode, setAcceptMode] = useState(null);
  const [acceptValue, setAcceptValue] = useState("");
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const standDetail = await getStandOverviewDetail(standId, { forceLive: isLiveStandId });
      setStand(standDetail);
      if (standDetail.standId) {
        const bridged = await getProjectByStandId(standDetail.standId, { forceLive: isLiveStandId });
        setProject(bridged);
      } else {
        setProject(null);
      }
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [standId, isLiveStandId]);

  useEffect(() => {
    (async () => {
      await load();
      try {
        setAllStandFiles(await getAllStandFiles());
      } catch {
        // Yardımcı bir zenginleştirme — başarısız olursa sayfa yine de çalışır.
      }
      try {
        // Alan etiketleri (fieldKey → "Beyan edilen yükseklik (m)") ve
        // requiresSystemReassign uyarısı buradan gelir; sabit listeden DEĞİL.
        setRevisableFields(await getRevisableFields({ forceLive: isLiveStandId }));
      } catch {
        // Etiket olmadan da fieldKey gösterilebilir.
      }
    })();
  }, [load, isLiveStandId]);

  function showSuccess(msg) {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  }

  const revision = project?.revisions?.find(r => r._id === revisionId && Array.isArray(r.messages));
  const fieldMeta = revision?.fieldKey ? revisableFields.find(f => f.key === revision.fieldKey) : null;

  async function runAction(fn, successText) {
    setActionLoading(true);
    try {
      await fn();
      await load();
      showSuccess(successText);
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      setActionLoading(false);
    }
  }

  async function handleReply() {
    if (!replyText.trim()) return;
    const ok = await runAction(
      () => addRevisionMessage(project.id, revisionId, replyText.trim(), "FDS Uzmanı", { forceLive: isLiveStandId }),
      t("standRevisionDetail.mesajGonderildi"),
    );
    if (ok) setReplyText("");
  }

  async function handleToggleStatus() {
    const next = revision.status === "resolved" ? "open" : "resolved";
    await runAction(
      () => setRevisionStatus(project.id, revisionId, next, { forceLive: isLiveStandId }),
      next === "resolved" ? t("standRevisionDetail.revizyonCozulduOlarakIsaretlendi") : t("standRevisionDetail.revizyonYenidenAcildi"),
    );
  }

  async function handleAccept() {
    const ok = await runAction(
      () => acceptRevision(project.id, revisionId, {
        value: acceptMode === "edit" ? acceptValue : null,
        apply: acceptMode !== "ack",
        forceLive: isLiveStandId,
      }),
      "Revizyon kabul edildi.",
    );
    if (ok) { setAcceptMode(null); setAcceptValue(""); }
  }

  async function handleReject() {
    const ok = await runAction(
      () => rejectRevision(project.id, revisionId, rejectReason, { forceLive: isLiveStandId }),
      t("standRevisionDetail.oneriReddedildiRevizyonAcikKaldi"),
    );
    if (ok) { setRejectModal(false); setRejectReason(""); }
  }

  async function handleDelete() {
    setActionLoading(true);
    try {
      await deleteRevision(project.id, revisionId, { forceLive: isLiveStandId });
      navigate(`/Management/Projects/Stands/${standId}`);
    } catch (e) {
      setError(e);
      setDeleteModal(false);
    } finally {
      setActionLoading(false);
    }
  }

  if (loading) return (
    <div className="page-content">
      <Container fluid className="text-center py-5"><Spinner color="primary" /></Container>
    </div>
  );

  if (error && !stand) return (
    <div className="page-content">
      <Container fluid>
        <ErrorAlert error={error} />
        <Button color="secondary" outline size="sm" onClick={() => navigate("/Management/Projects")}>
          <ArrowLeft size={14} className="me-1" />{" "}{t("standRevisionDetail.listeyeDon")}
        </Button>
      </Container>
    </div>
  );

  if (!stand) return null;

  if (!project) return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("standRevisionDetail.revizyon", { standName: stand.standName })} pageTitle={t("standRevisionDetail.projeler")} />
        <Alert color="secondary">{t("standRevisionDetail.buStandaBagliBirProje")}</Alert>
        <Button color="secondary" outline size="sm" onClick={() => navigate(`/Management/Projects/Stands/${standId}`)}>
          <ArrowLeft size={14} className="me-1" />{" "}{t("standRevisionDetail.standaDon")}
        </Button>
      </Container>
    </div>
  );

  if (!revision) return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("standRevisionDetail.revizyon", { standName: stand.standName })} pageTitle={t("standRevisionDetail.projeler")} />
        <Alert color="warning">{t("standRevisionDetail.revizyonBulunamadiSilinmisVeyaEski")}</Alert>
        <Button color="secondary" outline size="sm" onClick={() => navigate(`/Management/Projects/Stands/${standId}`)}>
          <ArrowLeft size={14} className="me-1" />{" "}{t("standRevisionDetail.standaDon")}
        </Button>
      </Container>
    </div>
  );

  const kindInfo = REVISION_TARGET_KIND[revision.targetKind];
  const statusInfo = REVISION_STATUS[revision.status];
  const openedByInfo = REVISION_OPENED_BY[revision.openedByRole];
  const targetFileName = resolveFileName(revision.standFileId, project, allStandFiles);
  const isResolved = revision.status === "resolved";
  // Sıra bizde: tamponda karar bekleyen bir değer (ya da yüklenmiş bir dosya) var.
  const awaitingUs = revision.status === "answered";
  // Projeye yazılacak bir alan var mı? Belge/serbest not revizyonlarında yoktur —
  // onlar SADECE "yazmadan kabul" (ACKNOWLEDGED) ile kapanabilir.
  const isWritable = revision.targetKind === "SPEC_FIELD" && !!revision.fieldKey;
  const hasProposal = !!revision.proposed;
  const targetLabel = revision.targetKind === "DOCUMENT"
    ? (targetFileName ?? t("standRevisionDetail.belge"))
    : (fieldMeta?.label ?? revision.fieldKey ?? t("standRevisionDetail.projeninGeneli"));

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("standRevisionDetail.revizyon", { standName: stand.standName })} pageTitle={t("standRevisionDetail.projeler")} />

        {successMsg && <Alert color="success">{successMsg}</Alert>}
        <ErrorAlert error={error} onDismiss={() => setError(null)} />

        {/* ─── Başlık Bar ─── */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <Button color="secondary" outline size="sm" onClick={() => navigate(`/Management/Projects/Stands/${standId}`)}>
            <ArrowLeft size={14} className="me-1" />{" "}{t("standRevisionDetail.standaDon")}
          </Button>
          <div className="d-flex gap-2">
            <Button color="info" outline size="sm" onClick={load} title={t("common:yenile")}>
              <RefreshCw size={14} />
            </Button>
            <Button
              color={isResolved ? "warning" : "success"}
              outline size="sm"
              onClick={handleToggleStatus}
              disabled={actionLoading}
              title={isResolved
                ? t("standRevisionDetail.revizyonuYenidenAc")
                : t("standRevisionDetail.kararKaydetmedenKapatBirTalebe")}
            >
              {isResolved
                ? <><RotateCcw size={14} className="me-1" />{" "}{t("standRevisionDetail.yenidenAc")}</>
                : <><CheckCircle size={14} className="me-1" />{" "}{t("standRevisionDetail.cozulduIsaretle")}</>}
            </Button>
            <Button color="danger" outline size="sm" onClick={() => setDeleteModal(true)}>
              <Trash2 size={14} />
            </Button>
          </div>
        </div>

        {/* ─── Metadata Kartı ─── */}
        <Card className="mb-3">
          <CardBody>
            <Row className="g-3">
              <Col md={2}>
                <div className="text-muted small">{t("standRevisionDetail.konu")}</div>
                <Badge color={kindInfo?.color ?? "secondary"}>{kindInfo?.label ?? revision.targetKind}</Badge>
              </Col>
              <Col md={2}>
                <div className="text-muted small">{t("standRevisionDetail.durum")}</div>
                <Badge color={statusInfo?.color ?? "secondary"}>{statusInfo?.label ?? revision.status}</Badge>
              </Col>
              <Col md={2}>
                <div className="text-muted small">{t("standRevisionDetail.acan")}</div>
                <Badge color={openedByInfo?.color ?? "secondary"}>{openedByInfo?.label ?? revision.openedByRole}</Badge>
              </Col>
              <Col md={3}>
                <div className="text-muted small">{t("standRevisionDetail.hedef")}</div>
                <div className="fw-semibold">{targetLabel}</div>
              </Col>
              <Col md={3}>
                <div className="text-muted small">{isResolved ? t("standRevisionDetail.cozuldu") : t("standRevisionDetail.sonGuncelleme")}</div>
                <div className="small">{fmtDate(isResolved ? revision.resolvedAt : revision.updatedAt)}</div>
                <div className="text-muted small">{t("standRevisionDetail.acildi")}{" "}{fmtDate(revision.createdAt)}</div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* ─── Karar Kartı — sıra bizdeyken. Tampon dolu demek "değer masada,
             projede HÂLÂ hiçbir şey değişmedi" demek; buradaki Kabul Et onu
             projeye yazan tek çağrıdır. ─── */}
        {!isResolved && (awaitingUs || hasProposal || !isWritable) && (
          <Card className={`mb-3 ${awaitingUs ? "border-warning" : ""}`}>
            <CardHeader className="d-flex align-items-center gap-2">
              <AlertTriangle size={15} className={awaitingUs ? "text-warning" : "text-muted"} />
              <h6 className="mb-0">{awaitingUs ? t("standRevisionDetail.kararBekliyor") : t("standRevisionDetail.karar")}</h6>
            </CardHeader>
            <CardBody>
              <p className="text-muted small">
                {isWritable
                  ? t("standRevisionDetail.buDegerHenuzProjeyeYazilmadi")
                  : t("standRevisionDetail.buRevizyondaProjeyeYazilacakBir")}
              </p>
              {fieldMeta?.requiresSystemReassign && (
                <Alert color="warning" className="py-2 small">
                  <strong>{fieldMeta.label}</strong>{" "}{t("standRevisionDetail.kabulEdilirseProje")}{" "}<strong>{t("standRevisionDetail.sistemAtamasi")}</strong>
                  {t("standRevisionDetail.kuyrugunaGeriDonerYukseklikBandi")}
                </Alert>
              )}
              <div className="d-flex flex-wrap gap-2">
                {isWritable && hasProposal && (
                  <Button color="success" size="sm" disabled={actionLoading}
                    onClick={() => { setAcceptMode("apply"); setAcceptValue(""); }}>
                    <ThumbsUp size={13} className="me-1" />{" "}{t("standRevisionDetail.kabulEtVeUygula")}
                  </Button>
                )}
                {isWritable && (
                  <Button color="info" size="sm" outline disabled={actionLoading}
                    onClick={() => { setAcceptMode("edit"); setAcceptValue(String(revision.proposed?.value ?? "")); }}>
                    <Pencil size={13} className="me-1" />{" "}{t("standRevisionDetail.duzenleyerekKabulEt")}
                  </Button>
                )}
                <Button color="secondary" size="sm" outline disabled={actionLoading}
                  onClick={() => { setAcceptMode("ack"); setAcceptValue(""); }}>
                  <CheckCircle size={13} className="me-1" />{" "}{t("standRevisionDetail.yazmadanKabulEt")}
                </Button>
                {hasProposal && (
                  <Button color="danger" size="sm" outline disabled={actionLoading}
                    onClick={() => { setRejectModal(true); setRejectReason(""); }}>
                    <XCircle size={13} className="me-1" />{" "}{t("standRevisionDetail.reddet")}
                  </Button>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {/* ─── Konuşma ─── */}
        <Card className="mb-3">
          <CardHeader>
            <h6 className="mb-0">{t("standRevisionDetail.konusma")}</h6>
          </CardHeader>
          <CardBody>
            <RevisionThread revision={revision} viewerRole="inspector" fieldLabel={fieldMeta?.label} />
          </CardBody>
        </Card>

        {/* ─── Yanıt Kutusu (sadece inceleyen kişi) ─── */}
        <Card className="mb-4">
          <CardBody>
            {isResolved ? (
              <Alert color="secondary" className="mb-0">
                {t("standRevisionDetail.buRevizyonCozulduOlarakIsaretlendi")}
              </Alert>
            ) : (
              <>
                <Input
                  type="textarea"
                  rows={3}
                  placeholder={t("standRevisionDetail.yanitiniziYazin")}
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  disabled={actionLoading}
                />
                <div className="d-flex justify-content-end mt-2">
                  <Button color="primary" size="sm" onClick={handleReply} disabled={actionLoading || !replyText.trim()}>
                    {actionLoading ? <Spinner size="sm" className="me-1" /> : <Send size={13} className="me-1" />}
                    {t("common:gonder")}
                  </Button>
                </div>
              </>
            )}
          </CardBody>
        </Card>

        {/* ─── MODAL: Kabul Onayı ─── */}
        <Modal isOpen={!!acceptMode} toggle={() => setAcceptMode(null)}>
          <ModalHeader toggle={() => setAcceptMode(null)}>
            {acceptMode === "edit" ? t("standRevisionDetail.duzenleyerekKabulEt") : acceptMode === "ack" ? t("standRevisionDetail.yazmadanKabulEt") : t("standRevisionDetail.kabulEtVeUygula")}
          </ModalHeader>
          <ModalBody>
            {acceptMode === "apply" && (
              <p className="mb-2">
                <strong>{fieldMeta?.label ?? revision.fieldKey}</strong>{" "}{t("standRevisionDetail.alaninaDekoratorunOnerdigiDegerYazilacak")}{" "}<strong>{fmtRevisionValue(revision.proposed?.value)}</strong>
              </p>
            )}
            {acceptMode === "ack" && (
              <p className="mb-2">
                {t("standRevisionDetail.revizyonKabulEdilmisSayilacakVe")}{" "}<strong>{t("standRevisionDetail.projeyeHicbirDegerYazilmayacak")}</strong>.
                {revision.targetKind === "DOCUMENT" && t("standRevisionDetail.dosyaOnayiAyriBirIslemdir")}
              </p>
            )}
            {acceptMode === "edit" && (
              <FormGroup>
                <Label>{fieldMeta?.label ?? revision.fieldKey}{" "}{t("standRevisionDetail.projeyeYazilacakDeger")}</Label>
                {fieldMeta?.valueType === "BOOLEAN" ? (
                  <Input type="select" value={acceptValue} onChange={e => setAcceptValue(e.target.value)}>
                    <option value="">{t("standRevisionDetail.secin")}</option>
                    <option value="true">{t("common:evet")}</option>
                    <option value="false">{t("common:hayir")}</option>
                  </Input>
                ) : (
                  <Input
                    type="number"
                    step={fieldMeta?.valueType === "INTEGER" ? "1" : "0.01"}
                    value={acceptValue}
                    onChange={e => setAcceptValue(e.target.value)}
                  />
                )}
                <div className="text-muted small mt-1">
                  {t("standRevisionDetail.dekoratorunOnerisiTampondaKalirSizin", { fmtRevisionValue: fmtRevisionValue(revision.proposed?.value) })}
                </div>
              </FormGroup>
            )}
            {fieldMeta?.requiresSystemReassign && acceptMode !== "ack" && (
              // ⚠️ 2026-08-28: bu geri dönüş artık SONUÇSUZ kalabiliyor. assign-system,
              //    projede onay bekleyen bir Proje Özel İzni varsa ya da fuarın zorunlu
              //    beyan formları doldurulmamışsa 400 dönüyor. Yani bu iki alanı kabul etmek,
              //    projeyi BAŞKA bir şey çözülene kadar kuyrukta park edebilir. Kabul etmeden
              //    önce söylüyoruz; 400'ü denemesi gereken kişi kullanıcı olmamalı.
              <Alert color="warning" className="py-2 small mb-0">
                {t("standRevisionDetail.kabulSonrasiProje")}{" "}<strong>{t("standRevisionDetail.sistemAtamasi")}</strong>{" "}{t("standRevisionDetail.kuyrugunaGeriDonerVeZorunlu")}
                <div className="mt-1">
                  {t("standRevisionDetail.sistemAtamasi2")}{" "}<strong>{t("standRevisionDetail.reddedilebilir")}</strong>{t("standRevisionDetail.projedeOnayBekleyenBir")}
                  <strong>{" "}{t("standRevisionDetail.projeOzelIzni")}</strong>{" "}{t("standRevisionDetail.varsaYaDaFuarin")}{" "}<strong>{t("standRevisionDetail.zorunluBeyanFormlari")}</strong>
                  {t("standRevisionDetail.doldurulmamissaKabulEttiktenSonraProje")}
                </div>
              </Alert>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setAcceptMode(null)}>{t("common:iptal")}</Button>
            <Button
              color="success"
              onClick={handleAccept}
              disabled={actionLoading || (acceptMode === "edit" && !String(acceptValue).trim())}
            >
              {actionLoading ? <Spinner size="sm" /> : t("standRevisionDetail.kabulEt")}
            </Button>
          </ModalFooter>
        </Modal>

        {/* ─── MODAL: Reddet ─── */}
        <Modal isOpen={rejectModal} toggle={() => setRejectModal(false)}>
          <ModalHeader toggle={() => setRejectModal(false)}>{t("standRevisionDetail.oneriyiReddet")}</ModalHeader>
          <ModalBody>
            <p className="text-muted small">
              {t("standRevisionDetail.oneriSilinirVeRevizyon")}{" "}<strong>{t("standRevisionDetail.tekrarAcik")}</strong>{" "}{t("standRevisionDetail.haleGelirDekoratorGerekceniziGorup")}
            </p>
            <FormGroup>
              <Label>{t("standRevisionDetail.gerekceZorunlu")}</Label>
              <Input
                type="textarea"
                rows={3}
                placeholder={t("standRevisionDetail.nedenReddedildiginiYazin")}
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setRejectModal(false)}>{t("common:iptal")}</Button>
            <Button color="danger" onClick={handleReject} disabled={actionLoading || !rejectReason.trim()}>
              {actionLoading ? <Spinner size="sm" /> : t("standRevisionDetail.reddet")}
            </Button>
          </ModalFooter>
        </Modal>

        {/* ─── MODAL: Sil Onay ─── */}
        <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
          <ModalHeader toggle={() => setDeleteModal(false)}>{t("standRevisionDetail.revizyonuSil")}</ModalHeader>
          <ModalBody>
            <Alert color="warning">
              {t("standRevisionDetail.buRevizyonKonusmasiniTumMesajlari")}
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setDeleteModal(false)}>{t("common:iptal")}</Button>
            <Button color="danger" onClick={handleDelete} disabled={actionLoading}>
              {actionLoading ? <Spinner size="sm" /> : t("common:sil")}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
}
